import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NumberFormatPreviewSchema,
  NumberSequenceSummarySchema,
  type NumberSequenceSummary,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

const SEQUENCES = '/api/v1/platform/number-sequences';
const KEY = ['platform', 'number-sequences'];

/**
 * Number sequences (SDD §15.1; FRD-CORE-035 to CORE-037): the series modules issue their business
 * identifiers from. The format is business configuration; changing it never reuses a number,
 * because the counter continues. A series that is not defined yet blocks the records numbered from
 * it, so it is flagged here.
 */
export function NumberSequencesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'platform.number_sequence:create');
  const canChange = hasCapability(session.data, 'platform.number_sequence:change');
  const [defining, setDefining] = useState<NumberSequenceSummary | null>(null);
  const [editing, setEditing] = useState<NumberSequenceSummary | null>(null);
  const sequences = useQuery({
    queryKey: KEY,
    queryFn: () => getData(SEQUENCES, z.array(NumberSequenceSummarySchema)),
  });
  const undefinedCount = (sequences.data ?? []).filter((s) => !s.configured).length;
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Number sequences"
        description="How business identifiers are formatted. A number once issued is never issued again."
      />
      {undefinedCount > 0 && (
        <Alert severity="warning" variant="outlined">
          {undefinedCount === 1 ? '1 series is' : `${String(undefinedCount)} series are`} not
          defined. Records numbered from {undefinedCount === 1 ? 'it' : 'them'} cannot be created
          until a format is set.
        </Alert>
      )}
      <Section>
        {sequences.isPending && <LoadingState label="Loading number sequences" />}
        {sequences.isError && <ErrorState message={sequences.error.message} />}
        {sequences.data?.length === 0 && <EmptyState title="No module issues numbers yet" />}
        {sequences.data !== undefined && sequences.data.length > 0 && (
          <Table size="small" aria-label="Number sequences">
            <TableHead>
              <TableRow>
                <TableCell>Series</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Next number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sequences.data.map((s) => (
                <TableRow key={`${s.code}/${s.scopeKey}`} hover>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.description ?? s.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.code}
                      {s.scopeKey === '' ? '' : ` [${s.scopeKey}]`}
                    </Typography>
                  </TableCell>
                  <TableCell>{s.format ?? '—'}</TableCell>
                  <TableCell>{s.nextPreview ?? '—'}</TableCell>
                  <TableCell>
                    {s.status === null ? (
                      <Chip size="small" variant="outlined" color="warning" label="Not defined" />
                    ) : (
                      <StatusChip status={s.status} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {!s.configured && canCreate && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setDefining(s);
                        }}
                      >
                        Define
                      </Button>
                    )}
                    {s.configured && canChange && (
                      <Button
                        size="small"
                        onClick={() => {
                          setEditing(s);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {defining !== null && (
        <SequenceDialog
          sequence={defining}
          onClose={() => {
            setDefining(null);
          }}
        />
      )}
      {editing !== null && (
        <SequenceDialog
          key={editing.code}
          sequence={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

function SequenceDialog({
  sequence,
  onClose,
}: {
  sequence: NumberSequenceSummary;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const defining = !sequence.configured;
  const [format, setFormat] = useState(sequence.format ?? '');
  const [startAt, setStartAt] = useState('1');
  const [status, setStatus] = useState<'active' | 'inactive'>(sequence.status ?? 'active');
  // The number the preview shows: the series' real next value, or the first number of a new one.
  const next = defining
    ? /^[1-9]\d*$/.test(startAt)
      ? startAt
      : '1'
    : (sequence.nextValue ?? '1');
  const preview = useQuery({
    queryKey: [...KEY, 'preview', format, next],
    enabled: format.trim() !== '',
    retry: false,
    queryFn: () =>
      getData(
        `${SEQUENCES}/preview?format=${encodeURIComponent(format.trim())}&next=${next}`,
        NumberFormatPreviewSchema,
      ),
  });
  const csrf = session.data?.csrfToken ?? '';
  const save = useMutation({
    mutationFn: () =>
      defining
        ? postJson(
            SEQUENCES,
            { code: sequence.code, format: format.trim(), startAt },
            NumberSequenceSummarySchema,
            csrf,
          )
        : patchJson(
            `${SEQUENCES}/${sequence.code}`,
            {
              ...(format.trim() !== sequence.format ? { format: format.trim() } : {}),
              ...(status !== sequence.status ? { status } : {}),
            },
            NumberSequenceSummarySchema,
            csrf,
            sequence.version ?? 0,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const unchanged = !defining && format.trim() === sequence.format && status === sequence.status;
  return (
    <FormDialog
      open
      title={defining ? `Define ${sequence.description ?? sequence.code}` : `Edit ${sequence.code}`}
      submitLabel={defining ? 'Define' : 'Save'}
      canSubmit={
        format.trim() !== '' && preview.isSuccess && !unchanged && /^[1-9]\d*$/.test(startAt)
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Format"
        required
        value={format}
        onChange={(e) => {
          setFormat(e.target.value);
        }}
        helperText="Literal text with {SEQ:n} (the number, padded to n digits) and optionally {YYYY} (the business year), e.g. EMP-{SEQ:5}."
      />
      <Typography variant="body2" role="status">
        {format.trim() === ''
          ? 'Enter a format to see the next number.'
          : preview.isSuccess
            ? `The next number would look like ${preview.data.preview}.`
            : preview.isError
              ? preview.error.message
              : 'Checking…'}
      </Typography>
      {defining ? (
        <TextField
          label="First number"
          value={startAt}
          onChange={(e) => {
            setStartAt(e.target.value);
          }}
          helperText="Usually 1. Set a higher number to continue a series used before this system."
        />
      ) : (
        <>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value === 'inactive' ? 'inactive' : 'active');
            }}
            helperText="An inactive series issues no numbers, so its records cannot be created."
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <Alert severity="info" variant="outlined">
            Next unissued number: {sequence.nextValue}. Changing the format continues from it.
          </Alert>
        </>
      )}
    </FormDialog>
  );
}
